/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { mcpApprovalEnsureServerReview } from "../funcs/mcpApprovalEnsureServerReview.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { ApprovalRequestSummary } from "../models/components/approvalrequestsummary.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  EnsureMcpServerReviewRequest,
  EnsureMcpServerReviewSecurity,
} from "../models/operations/ensuremcpserverreview.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type EnsureMcpServerReviewMutationVariables = {
  request: EnsureMcpServerReviewRequest;
  security?: EnsureMcpServerReviewSecurity | undefined;
  options?: RequestOptions;
};

export type EnsureMcpServerReviewMutationData = ApprovalRequestSummary;

export type EnsureMcpServerReviewMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * ensureServerReview mcpApproval
 *
 * @remarks
 * Resolve the evidence dossier for a server URL, opening one when none exists. Gathers evidence without recording any ask or decision, so a server can be inspected before — or without — anyone requesting it.
 */
export function useEnsureMcpServerReviewMutation(
  options?: MutationHookOptions<
    EnsureMcpServerReviewMutationData,
    EnsureMcpServerReviewMutationError,
    EnsureMcpServerReviewMutationVariables
  >,
): UseMutationResult<
  EnsureMcpServerReviewMutationData,
  EnsureMcpServerReviewMutationError,
  EnsureMcpServerReviewMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildEnsureMcpServerReviewMutation(client, options),
    ...options,
  });
}

export function mutationKeyEnsureMcpServerReview(): MutationKey {
  return ["@gram/client", "mcpApproval", "ensureServerReview"];
}

export function buildEnsureMcpServerReviewMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: EnsureMcpServerReviewMutationVariables,
  ) => Promise<EnsureMcpServerReviewMutationData>;
} {
  return {
    mutationKey: mutationKeyEnsureMcpServerReview(),
    mutationFn: function ensureMcpServerReviewMutationFn({
      request,
      security,
      options,
    }): Promise<EnsureMcpServerReviewMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(mcpApprovalEnsureServerReview(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
